/**
 * FadeImageText — custom element for the "Fade Image + Text" section.
 *
 * Responsibilities:
 *  1. Fade-in — IntersectionObserver triggers .fit__fade → .is-visible
 *     when each block enters the viewport.
 *  2. Video blocks — handles Shopify-hosted and YouTube videos:
 *       • Autoplay ON  → plays inline (muted). YouTube via iframe embed.
 *       • Autoplay OFF → shows thumbnail + play button; click opens fullscreen
 *         modal (Shopify with sound+controls, YouTube with autoplay iframe).
 *  3. Page-content modals — opened by image or button blocks.
 *     On open, scrolls to the configured anchor ID inside the page content.
 *  4. All modals close on: close button click, backdrop click, Escape key.
 */

if (!customElements.get('fade-image-text')) {
  class FadeImageText extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        this._initFade();
        this._initVideoBlocks();
        this._initPageModals();
      });
    }

    // ── 1. Fade-in ──────────────────────────────────────────────────────────

    _initFade() {
      if (this.dataset.fade !== 'true') return;

      const els = [...this.querySelectorAll('.fit__fade')];
      if (!els.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Toggle each time — fades in when entering, resets when leaving
            entry.target.classList.toggle('is-visible', entry.isIntersecting);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
      );

      els.forEach((el) => observer.observe(el));
    }

    // ── 2. Video blocks ─────────────────────────────────────────────────────

    _initVideoBlocks() {
      this.querySelectorAll('.fit__block--video').forEach((block) => {
        const autoplay   = block.dataset.autoplay === 'true';
        const youtubeId  = block.dataset.youtubeId || null;
        const hostedVideo = block.querySelector('.fit__video');
        const playBtn    = block.querySelector('.fit__play-btn');
        const modal      = block.querySelector('.fit__modal--video');

        // ── Autoplay: Shopify hosted ──
        if (autoplay && hostedVideo) {
          hostedVideo.play().catch(() => {
            // If autoplay is blocked show poster (it's already the img[poster] fallback)
          });
        }

        // ── Autoplay: YouTube iframe ──
        // Nothing to do — the iframe src already has autoplay params set from Liquid.

        // ── Manual play → open fullscreen modal ──
        if (!autoplay && playBtn && modal) {
          playBtn.addEventListener('click', () => this._openVideoModal(modal, hostedVideo, youtubeId));
        }
      });
    }

    _openVideoModal(modal, hostedVideo, youtubeId) {
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      if (youtubeId) {
        const iframe = modal.querySelector('.fit__modal-yt');
        if (iframe && !iframe.src) {
          iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`;
        }
      } else if (hostedVideo) {
        const mVid = modal.querySelector('.fit__modal-video');
        if (mVid) {
          mVid.currentTime = 0;
          mVid.play().catch(() => {});
        }
      }

      this._bindModalClose(modal, () => {
        if (youtubeId) {
          const iframe = modal.querySelector('.fit__modal-yt');
          if (iframe) iframe.src = ''; // stops YouTube playback
        } else {
          const mVid = modal.querySelector('.fit__modal-video');
          if (mVid) { mVid.pause(); mVid.currentTime = 0; }
        }
      });
    }

    // ── 3. Page-content modals ──────────────────────────────────────────────

    _initPageModals() {
      this.querySelectorAll('[data-role="open-page-modal"]').forEach((trigger) => {
        const modalId = trigger.dataset.modal;
        const modal   = document.getElementById(modalId);
        if (!modal) return;

        trigger.addEventListener('click', () => {
          modal.removeAttribute('hidden');
          document.body.style.overflow = 'hidden';

          // Scroll to anchor inside the modal content
          const anchor = modal.dataset.anchor;
          if (anchor) {
            requestAnimationFrame(() => {
              const target = modal.querySelector('#' + CSS.escape(anchor));
              if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }

          this._bindModalClose(modal);
        });
      });
    }

    // ── Shared modal close logic ────────────────────────────────────────────

    _bindModalClose(modal, onClose) {
      const close = () => {
        modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        onClose?.();
        // Clean up this listener set
        document.removeEventListener('keydown', escHandler);
      };

      const escHandler = (e) => {
        if (e.key === 'Escape' && !modal.hasAttribute('hidden')) close();
      };

      modal.querySelector('.fit__modal-close')?.addEventListener('click', close, { once: true });
      modal.addEventListener('click', (e) => { if (e.target === modal) close(); }, { once: true });
      document.addEventListener('keydown', escHandler);
    }

    // ── YouTube ID extraction (used in Liquid too, but JS is the fallback) ──

    static youtubeId(url) {
      if (!url) return null;
      const m = url.match(
        /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/
      );
      return m ? m[1] : null;
    }
  }

  customElements.define('fade-image-text', FadeImageText);
}
